import { useApolloClient } from '@apollo/client/react'
import { useSetImmerAtom } from 'jotai-immer'
import { useContext } from 'react'
import { Button as AriaButton } from 'react-aria-components'
import { toast } from 'react-toastify'
import { useAsyncFn } from 'react-use'
import IcRoundFlag from '~icons/ic/round-flag'
import LucideEllipsis from '~icons/lucide/ellipsis'
import { UIButton } from 'src/components/ui/Button'
import { UILink } from 'src/components/ui/Link'
import { UIMenu, UIMenuItem, UIMenuTrigger } from 'src/components/ui/Menu'
import Text from 'src/components/ui/Text'
import UserAvatar from 'src/components/UserAvatar'
import { useCommentsRedesignExperiment } from 'src/experiments/experiments/2605/commentsRedesign'
import { type Message, RefType } from 'src/services/apollo/generated/graphql'
import { useMe } from 'src/services/helpers/user'
import { getCommentTextModerationInfo } from 'src/utils/comment'
import { cx } from 'src/utils/cx'
import dayjs from 'src/utils/dayjs'
import { formatRelativeDate } from 'src/utils/formatTime'
import { track } from 'src/utils/tracker'
import { createProfileUrl, getUserDisplayName } from 'src/utils/user'
import { openSignModalRAC } from '../Account/SignModalRAC'
import { dialogReport } from '../Report/ReportDialog'
import { UIDialog } from '../ui/Modal'
import { CommentContent } from './CommentContent'
import { useRegisterCommentRef } from './commentsScroll'
import {
  type CanDeleteMessage,
  CommentContext,
  createComposerStateAtom,
} from './context'
import { deleteMessage } from './mutations'
import {
  NulledComment,
  RepliedComment,
  ReplyTargetInlineLabel,
} from './RepliedComment'

/**
 * A single comment
 *
 * Can be used standalone or within a `<CommentProvider>`.
 */
export function CommentItem(
  props: StyleProps & {
    message: Message
    refType?: RefType
    canDeleteMessage?: CanDeleteMessage
    onDelete?: () => void
  },
) {
  const {
    id,
    author,
    createdAt,
    content,
    replyToMessageId = '',
    extra,
  } = props.message

  const { isFlagged } = getCommentTextModerationInfo(extra)
  const authorLink = author ? createProfileUrl(author) : undefined
  const { me, beingAdmin } = useMe()
  const beingMyMessage = me != null && me.id === author?.id
  const ctx = useContext(CommentContext)
  const refType = props.refType ?? ctx?.refType
  const canDeleteMessage = props.canDeleteMessage ?? ctx?.canDeleteMessage
  const updateComposerState = useSetImmerAtom(
    ctx?.composerStateAtom ?? createComposerStateAtom(),
  )
  const { isTreatment } = useCommentsRedesignExperiment()
  const beingCreator =
    isTreatment && ctx?.creatorId != null && author?.id === ctx.creatorId
  const canDelete =
    (beingMyMessage || beingAdmin || canDeleteMessage?.(props.message)) &&
    !!refType

  // Same reason as in `CommentProvider`: when a `<CommentItem>` is rendered
  // standalone (no surrounding `CommentProvider`) inside a route-scoped
  // `<ApolloProvider>` subtree, the delete mutation must run against the
  // route-scoped client too, otherwise the cache entry the surrounding
  // list reads from never sees the eviction.
  const apolloClient = useApolloClient()

  const [{ loading: deleting }, doDeleteMessage] = useAsyncFn(async () => {
    try {
      if (ctx) {
        await ctx.deleteMessage(id, refType)
      } else if (props.refType) {
        await deleteMessage(id, props.refType, apolloClient)
        props.onDelete?.()
      }
    } catch {
      toast.error(<Text i18nKey="comment:notice.delete.failed" />)
    }
  }, [apolloClient, ctx, id, props, refType])

  async function toDeleteMessage() {
    if (!refType) return

    const toProceed = await UIDialog.confirm({
      heading: <Text i18nKey="comment:delete.confirm-dialog.title" />,
      content: <Text i18nKey="comment:delete.confirm-dialog.description" />,
    })

    if (toProceed) {
      await doDeleteMessage()
    }
  }

  function startReply() {
    if (!me) return openSignModalRAC()
    updateComposerState(state => {
      state.replyTo = id
    })
  }

  const registerRef = useRegisterCommentRef(id)

  return (
    <div
      ref={registerRef}
      className={cx(
        'grid grid-cols-[auto_1fr] items-start gap-4 text-sm',
        deleting && 'opacity-50 pointer-events-none',
        props.className,
      )}
      style={props.style}
    >
      <UILink
        href={authorLink}
        // Override the ghost-variant hover bg (squared frame around round
        // avatar) + underline (visible on initials), and force pointer cursor
        // on descendants — UserAvatarImage's initials placeholder sets its
        // own cursor-default.
        className={
          isTreatment
            ? cx(
                'hover:bg-transparent dark:hover:bg-transparent hover:no-underline',
                authorLink
                  ? '[&_*]:!cursor-pointer'
                  : 'cursor-default [&_*]:!cursor-default',
              )
            : undefined
        }
        onPress={() => track('user_link_click')}
      >
        <UserAvatar user={author} className="size-10" />
      </UILink>
      <div className={cx('flex flex-col', isTreatment && 'min-w-0')}>
        <div
          className={
            isTreatment ? 'flex items-center gap-x-2 min-w-0' : 'flex gap-4'
          }
        >
          <UILink
            href={authorLink}
            className={cx(
              'px-1 -mx-1 font-semibold rounded',
              isTreatment && 'min-w-0 truncate',
              !authorLink && 'cursor-default',
            )}
            onPress={() => track('user_link_click')}
          >
            {getUserDisplayName(author)}
          </UILink>
          {beingCreator && (
            <span className="flex-shrink-0 px-1.5 py-0.5 text-xs font-medium rounded bg-black/5 dark:bg-white/5 text-neutral-700 dark:text-neutral-300">
              <Text i18nKey="comment:creator-badge" defaults="Creator" />
            </span>
          )}
          <time
            dateTime={dayjs(createdAt).toISOString()}
            title={isTreatment ? dayjs(createdAt).format('lll') : undefined}
            className="flex-shrink-0 text-neutral-500"
          >
            {isTreatment
              ? formatRelativeDate(createdAt)
              : dayjs(createdAt).format('lll')}
          </time>
        </div>
        {isTreatment && replyToMessageId && (
          <ReplyTargetInlineLabel
            messageId={replyToMessageId}
            className="mt-2"
          />
        )}
        {replyToMessageId && (
          <RepliedComment
            messageId={replyToMessageId}
            className={isTreatment ? 'mt-1 mb-1' : 'mt-2 mb-1'}
          />
        )}
        {isFlagged ? (
          <NulledComment
            displayText={<Text i18nKey="comment:content.flagged-hidden" />}
            hoverText={me ? content : undefined}
          />
        ) : (
          <CommentContent
            className={cx(
              'text-neutral-800 dark:text-neutral-200',
              isTreatment && 'leading-relaxed',
            )}
            content={content}
          />
        )}
        {isTreatment ? (
          <div className="mt-1 flex items-center gap-1">
            {ctx && !isFlagged && (
              <UIButton
                variant="ghost"
                size="small"
                className="px-2 -ml-2 py-1 -mt-1 font-semibold text-black dark:text-white"
                onPress={startReply}
              >
                <Text i18nKey="comment:reply.label" />
              </UIButton>
            )}
            {(canDelete || (me && !beingMyMessage)) && (
              <UIMenuTrigger>
                <AriaButton
                  aria-label="More actions"
                  className="grid place-items-center size-7 -mt-1 rounded text-neutral-400 hover:text-neutral-700 dark:text-neutral-500 dark:hover:text-neutral-300 hover:bg-black/5 dark:hover:bg-white/5"
                >
                  <LucideEllipsis className="size-4" />
                </AriaButton>
                <UIMenu placement="bottom right">
                  {me && !beingMyMessage && (
                    <UIMenuItem
                      icon={<IcRoundFlag />}
                      onAction={() => dialogReport(RefType.Message, id)}
                    >
                      <Text i18nKey="artwork:action.report" />
                    </UIMenuItem>
                  )}
                  {canDelete && (
                    <UIMenuItem color="danger" onAction={toDeleteMessage}>
                      <Text i18nKey="action:delete.label" />
                    </UIMenuItem>
                  )}
                </UIMenu>
              </UIMenuTrigger>
            )}
          </div>
        ) : (
          <div className="mt-1 flex gap-2">
            {ctx && !isFlagged && (
              <UIButton
                variant="ghost"
                size="small"
                className="px-2 -ml-2 py-1 -mt-1 text-black dark:text-white"
                onPress={startReply}
              >
                <Text i18nKey="comment:reply.label" />
              </UIButton>
            )}
            {me && !beingMyMessage && (
              <UIButton
                variant="ghost"
                size="small"
                busy="auto"
                className="px-2 -ml-2 py-1 -mt-1 text-black dark:text-white"
                onPress={() => dialogReport(RefType.Message, id)}
              >
                <Text i18nKey="artwork:action.report" />
              </UIButton>
            )}
            {canDelete && (
              <UIButton
                variant="ghost"
                size="small"
                color="danger"
                className="px-2 -ml-2 py-1 -mt-1 text-black hover:text-red-600 dark:text-white dark:hover:text-red-500"
                onPress={toDeleteMessage}
              >
                <Text i18nKey="action:delete.label" />
              </UIButton>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
